import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "./Notification";
import { FOLDERS, SIDEBAR } from "../data/finder";
import { emptyBin } from "../redux/windowSlice";
import finderIcon from "../assets/icons/dock/finder.png";
import binIcon from "../assets/icons/dock/trash-full.png";
import TrafficLights from "./TrafficLights";
import WindowFrame from "./WindowFrame";
import useGenie from "../hooks/useGenie";
import { ReactComponent as Recents } from "../assets/svgs/sf-symbols/finder/clock.svg";
import { ReactComponent as Applications } from "../assets/svgs/sf-symbols/finder/appstore.svg";
import { ReactComponent as Desktop } from "../assets/svgs/sf-symbols/finder/menubar.dock.rectangle.svg";
import { ReactComponent as Documents } from "../assets/svgs/sf-symbols/finder/document.svg";
import { ReactComponent as Downloads } from "../assets/svgs/sf-symbols/finder/arrow.down.circle.svg";
import { ReactComponent as Back } from "../assets/svgs/sf-symbols/finder/chevron.left.svg";
import { ReactComponent as Forward } from "../assets/svgs/sf-symbols/finder/chevron.right.svg";
import { ReactComponent as IconView } from "../assets/svgs/sf-symbols/finder/square.grid.2x2.svg";
import { ReactComponent as ListView } from "../assets/svgs/sf-symbols/finder/list.bullet.svg";
import { ReactComponent as Share } from "../assets/svgs/sf-symbols/finder/square.and.arrow.up.svg";
import { ReactComponent as Tag } from "../assets/svgs/sf-symbols/finder/tag.svg";
import { ReactComponent as More } from "../assets/svgs/sf-symbols/finder/ellipsis.circle.svg";
import { ReactComponent as Search } from "../assets/svgs/sf-symbols/finder/magnifyingglass.svg";

const SIDEBAR_ICONS = {
  recents: Recents,
  applications: Applications,
  desktop: Desktop,
  documents: Documents,
  downloads: Downloads,
};

// Double-clicking an item: folders open in place, everything else does what it says. What's in
// the Bin only says why it won't open.
const openItem = (item, go, alert) => {
  const { open } = item;
  if (open.binned)
    alert({
      icon: finderIcon,
      title: `“${item.name}” can’t be opened because it’s in the Bin.`,
      message: "To use this item, first drag it out of the Bin.",
      confirm: "OK",
    });
  else if (open.folder) go(open.folder);
  else if (open.url) window.open(open.url, "_blank", "noopener");
  else if (open.resume !== undefined || open.app === "Notes")
    window.dispatchEvent(new CustomEvent("open-resume", { detail: { anchor: open.resume } }));
  else if (open.app === "Spotify") window.dispatchEvent(new Event("open-spotify"));
  else if (open.app === "Finder") notify("You're already in Finder.");
  else if (open.app === "Google Chrome") window.dispatchEvent(new Event("open-chrome"));
  else if (open.app === "Ghostty") window.dispatchEvent(new Event("open-ghostty"));
  else if (open.app === "OrbStack") window.dispatchEvent(new Event("open-orbstack"));
  else if (open.app === "Zed") window.dispatchEvent(new Event("open-zed"));
  else notify("That app doesn't open on this Mac. Documents › Work Experience jumps straight into my resume.");
};

// A macOS alert over the window: an icon, what happened, and its buttons (the default one blue).
// Return takes the default, Escape cancels.
const Alert = ({ icon, title, message, confirm, cancel, onConfirm, onCancel }) => {
  const button = useRef(null);
  useEffect(() => button.current && button.current.focus({ preventScroll: true }), []);
  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Escape") {
      e.preventDefault();
      (onCancel || onConfirm)();
    }
  };
  return (
    <div className="fw-alert-layer" onKeyDown={onKeyDown} onClick={(e) => e.stopPropagation()}>
      <div className="fw-alert" role="alertdialog" aria-modal="true" aria-label={title}>
        <img src={icon} alt="" draggable={false} />
        <p className="fw-alert-title">{title}</p>
        {message && <p className="fw-alert-message">{message}</p>}
        <div className="fw-alert-buttons">
          {cancel && (
            <button type="button" onClick={onCancel}>
              {cancel}
            </button>
          )}
          <button type="button" ref={button} className="is-default" onClick={onConfirm}>
            {confirm}
          </button>
        </div>
      </div>
    </div>
  );
};

// Default frame: up to 860 x 520, offset left of center so an edge of it shows beside Notes.
const finderFrame = (ws) => {
  const w = Math.min(860, ws.w - 140);
  const h = Math.min(520, ws.h - 90);
  return { x: (ws.w - w) / 2 - 170, y: (ws.h - h) / 2 + 16, w, h };
};

const FinderWindow = ({ workspace, open, front, onFocus, onClose }) => {
  const [exit, setExit] = useState("closed");
  const genie = useGenie({ app: "Finder", open: open, exit });
  const [maximized, setMaximized] = useState(false);
  const [history, setHistory] = useState({ stack: ["documents"], index: 0 });
  const [view, setView] = useState("icons");
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [alert, setAlert] = useState(null);
  const binEmptied = useSelector((state) => state.window.binEmptied);
  const dispatch = useDispatch();

  const current = history.stack[history.index];
  const folder = FOLDERS[current];
  const items = useMemo(() => {
    const all = folder.bin && binEmptied ? [] : folder.items;
    return query ? all.filter((i) => i.name.toLowerCase().includes(query.toLowerCase())) : all;
  }, [folder, query, binEmptied]);

  const go = (id) => {
    if (id === current) return;
    setHistory(({ stack, index }) => ({ stack: [...stack.slice(0, index + 1), id], index: index + 1 }));
    setSelected(null);
    setQuery("");
  };
  const back = () => history.index > 0 && setHistory((h) => ({ ...h, index: h.index - 1 }));
  const forward = () => history.index < history.stack.length - 1 && setHistory((h) => ({ ...h, index: h.index + 1 }));

  useEffect(() => setSelected(null), [current]);

  // The Dock's Bin opens Finder at the Bin.
  const goRef = useRef(go);
  goRef.current = go;
  useEffect(() => {
    const onOpen = (e) => e.detail && e.detail.folder && goRef.current(e.detail.folder);
    window.addEventListener("open-finder", onOpen);
    return () => window.removeEventListener("open-finder", onOpen);
  }, []);

  const askToEmpty = () =>
    setAlert({
      icon: binIcon,
      title: "Are you sure you want to permanently erase the items in the Bin?",
      message: "You can’t undo this action.",
      confirm: "Empty Bin",
      cancel: "Cancel",
      onConfirm: () => {
        dispatch(emptyBin());
        setSelected(null);
      },
    });

  const hide = (how) => {
    setExit(how);
    if (how === "closed") setMaximized(false);
    onClose();
  };

  // Finder's shortcuts: ⌘O / ⌘↓ open, ⌘↑ enclosing folder, ⌘[ / ⌘] back and forward.
  const onKeyDown = (e) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    const item = items.find((i) => i.name === selected);
    if ((e.key === "o" || e.key === "ArrowDown") && item) {
      e.preventDefault();
      openItem(item, go, setAlert);
    } else if (e.key === "ArrowUp" && folder.parent) {
      e.preventDefault();
      go(folder.parent);
    } else if (e.key === "[") {
      e.preventDefault();
      back();
    } else if (e.key === "]") {
      e.preventDefault();
      forward();
    }
  };

  const state = open ? "nw--open" : `nw--${exit}`;
  // A plain render function, not a component: a component defined here would remount on every
  // render, and the first click of a double-click would replace the element being clicked.
  const renderItem = (item) => {
    const isSelected = selected === item.name;
    const props = {
      type: "button",
      onClick: (e) => {
        e.stopPropagation();
        setSelected(item.name);
      },
      onDoubleClick: () => openItem(item, go, setAlert),
      "aria-pressed": isSelected,
    };
    return view === "icons" ? (
      <button key={item.name} {...props} className={`fw-icon ${isSelected ? "is-selected" : ""}`}>
        <span className="fw-icon-img">
          <img src={item.icon} alt="" draggable={false} />
        </span>
        <span className="fw-icon-label">{item.name}</span>
      </button>
    ) : (
      <button key={item.name} {...props} className={`fw-row ${isSelected ? "is-selected" : ""}`}>
        <span className="fw-row-name">
          <img src={item.icon} alt="" draggable={false} />
          {item.name}
        </span>
        <span className="fw-row-kind">{item.kind}</span>
      </button>
    );
  };

  return (
    <WindowFrame
      workspace={workspace}
      initial={finderFrame}
      min={{ w: 620, h: 340 }}
      open={open}
      front={front}
      zoomed={maximized}
      onFocus={onFocus}
      onToggleZoom={() => setMaximized((m) => !m)}
    >
      <div
        role="dialog"
        aria-label={`Finder, ${folder.title}`}
        aria-hidden={!open}
        ref={genie.ref}
        className={`nw fw ${state} ${maximized ? "nw--max" : ""} ${front ? "is-front" : ""} ${
          genie.hidden ? "is-genie-hidden" : ""
        }`}
        onKeyDown={onKeyDown}
      >
        <div className="traffic">
          <TrafficLights
            onClose={() => hide("closed")}
            onMinimize={() => genie.minimize(() => hide("minimized"))}
            onPrepare={genie.prepare}
            onZoom={() => setMaximized(!maximized)}
            zoomed={maximized}
          />
        </div>

        <aside className="nw-sidebar fw-sidebar no-select">
          {SIDEBAR.map((section) => (
            <div key={section.title}>
              <p className="nw-section">{section.title}</p>
              {section.items.map((id) => {
                const Icon = SIDEBAR_ICONS[id];
                const active = id === current || FOLDERS[current].parent === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => go(id)}
                    className={`fw-side-item ${active ? "is-active" : ""}`}
                    aria-current={active}
                  >
                    <Icon className="fw-side-icon" aria-hidden="true" />
                    {FOLDERS[id].title}
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        <main className="nw-main fw-main">
          <div className="fw-toolbar no-select">
            <div className="nw-capsule glass glass--light fw-nav">
              <button type="button" onClick={back} disabled={history.index === 0} aria-label="Back">
                <Back />
              </button>
              <button
                type="button"
                onClick={forward}
                disabled={history.index === history.stack.length - 1}
                aria-label="Forward"
              >
                <Forward />
              </button>
            </div>
            <h2 className="fw-title">{folder.title}</h2>
            <div className="nw-capsule glass glass--light fw-views" role="group" aria-label="View">
              <button
                type="button"
                onClick={() => setView("icons")}
                aria-pressed={view === "icons"}
                aria-label="as Icons"
              >
                <IconView />
              </button>
              <button type="button" onClick={() => setView("list")} aria-pressed={view === "list"} aria-label="as List">
                <ListView />
              </button>
            </div>
            <div className="nw-capsule glass glass--light fw-actions" aria-hidden="true">
              <span>
                <Share />
              </span>
              <span>
                <Tag />
              </span>
              <span>
                <More />
              </span>
            </div>
            <label className="nw-capsule glass glass--light fw-search">
              <Search aria-hidden="true" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                aria-label={`Search ${folder.title}`}
              />
            </label>
          </div>

          {folder.bin && (
            <div className="fw-binbar no-select">
              <span>Bin</span>
              <button type="button" onClick={askToEmpty} disabled={binEmptied}>
                Empty
              </button>
            </div>
          )}

          <div className="fw-content" onClick={() => setSelected(null)}>
            {view === "list" && (
              <div className="fw-list-head">
                <span>Name</span>
                <span>Kind</span>
              </div>
            )}
            <div className={view === "icons" ? "fw-grid" : "fw-list"}>{items.map(renderItem)}</div>
            {!items.length && <p className="fw-empty">{query ? "No Results" : "This folder is empty."}</p>}
          </div>
        </main>

        {alert && (
          <Alert
            {...alert}
            onConfirm={() => {
              setAlert(null);
              if (alert.onConfirm) alert.onConfirm();
            }}
            onCancel={alert.cancel ? () => setAlert(null) : undefined}
          />
        )}
      </div>
    </WindowFrame>
  );
};

export default FinderWindow;
